/* eslint-disable react/prop-types */
import { ExternalFontController } from 'controllers/ExternalFontController';
import useWebSocket from 'hooks/useWebSocket';
import {
  IAvailableExternalInputModel,
  IFontTestDossierDataInputModel,
  IUpdateFontStatusInputModel,
} from 'inputModels';
import { Props } from 'props';
import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react';
import {
  IAvailableExternalFontViewModel,
  IExternalFontExecutionViewModel,
} from 'viewModels';
import { useAuth } from './auth';
import { TypeUser } from 'types/enums';
import { notification } from 'antd';

type ContextProps = {
  externalFonts: IAvailableExternalFontViewModel[];
  isLoadingFonts: boolean;
  isLoadingDetails: boolean;
  fetchExternalFonts: () => Promise<void>;
  fetchExternalFontDetails: () => Promise<void>;
  updateFontStatus: (input: IUpdateFontStatusInputModel) => Promise<void>;
  executeFontTest: (data: IFontTestDossierDataInputModel) => Promise<string>;
  tests: IExternalFontExecutionViewModel[];
  clearTestsForFont: (fontKey: string) => void;
  clearFontMetrics: () => Promise<void>;
  editExternalFont: (input: IAvailableExternalInputModel) => Promise<void>;
};

const ExternalFontContext = createContext<ContextProps>({} as ContextProps);

const ExternalFontProvider: React.FC<Props> = ({ children }) => {
  const { user, logged } = useAuth();
  const [tests, setTests] = useState<IExternalFontExecutionViewModel[]>([]);

  const [externalFonts, setExternalFonts] = useState<
    IAvailableExternalFontViewModel[]
  >([]);
  const [isLoadingFonts, setIsLoadingFonts] = useState(true);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  // Fetch basic font data
  const fetchExternalFonts = useCallback(async () => {
    setIsLoadingFonts(true);
    try {
      const response = await ExternalFontController.getExternalFonts();
      setExternalFonts(response.data as IAvailableExternalFontViewModel[]);
    } catch (error) {
      console.error('Error fetching external fonts:', error);
    } finally {
      setIsLoadingFonts(false);
    }
  }, []);

  // Fetch detailed font metrics
  const fetchExternalFontDetails = useCallback(async () => {
    setIsLoadingDetails(true);
    try {
      const response = await ExternalFontController.getExternalFontsDetails();
      const details = response.data as IAvailableExternalFontViewModel[];
      setExternalFonts(prevFonts =>
        prevFonts.map(font => {
          const detail = details.find(d => d.key === font.key);
          if (detail) {
            return {
              ...font,
              successRate: detail.successRate ?? font.successRate,
              executionCount: detail.executionCount ?? font.executionCount,
              averageExecutionTimeSeconds:
                detail.averageExecutionTimeSeconds ??
                font.averageExecutionTimeSeconds,
              disabledReason: detail.disabledReason ?? font.disabledReason,
            };
          }
          return font;
        })
      );
    } catch (error) {
      console.error('Error fetching external font details:', error);
    } finally {
      setIsLoadingDetails(false);
    }
  }, []);

  // Update font status
  const updateFontStatus = useCallback(
    async (input: IUpdateFontStatusInputModel) => {
      try {
        await ExternalFontController.updateExternalFont(input);
        setExternalFonts(prevFonts =>
          prevFonts.map(font =>
            font.key === input.key
              ? {
                  ...font,
                  disabled: input.disabled,
                  disabledReason: input.disabledReason,
                }
              : font
          )
        );
      } catch (error) {
        console.error('Error updating font status:', error);
        throw error;
      }
    },
    []
  );

  // Execute font test
  const executeFontTest = useCallback(
    async (inputData: IFontTestDossierDataInputModel) => {
      try {
        const response = await ExternalFontController.executeFontTest(
          inputData
        );
        const execution: IExternalFontExecutionViewModel = {
          id: response.data.id,
          fontKey: inputData.font,
          executionTime: new Date(),
          success: undefined,
          executionTimeSeconds: undefined,
          result: undefined,
          running: true,
        };
        setTests(prev => {
          return [execution, ...prev]; // Keep all tests, don't filter
        });
        return execution.id;
      } catch (error) {
        console.error('Error executing font test:', error);
        throw error;
      }
    },
    []
  );

  // Clear tests for a specific fontKey
  const clearTestsForFont = useCallback((fontKey: string) => {
    setTests(prev => {
      return prev.filter(test => test.fontKey !== fontKey);
    });
  }, []);

  // Clear all font metrics
  const clearFontMetrics = useCallback(async () => {
    try {
      await ExternalFontController.cleanFontsMetrics();
      setExternalFonts(prevFonts =>
        prevFonts.map(font => ({
          ...font,
          successRate: undefined,
          executionCount: undefined,
          averageExecutionTimeSeconds: undefined,
          disabledReason: undefined,
        }))
      );
    } catch (error) {
      console.error('Error clearing font metrics:', error);
      throw error;
    }
  }, []);

  const editExternalFont = useCallback(
    async (input: IAvailableExternalInputModel) => {
      try {
        await ExternalFontController.editExternalFont(input);
        setExternalFonts(prevFonts =>
          prevFonts.map(font =>
            font.key === input.key
              ? {
                  ...font,
                  name: input.name,
                  type: input.type,
                  disabled: input.disabled,
                  disabledReason: input.disabledReason,
                  requiredParameters: input.requiredParameters,
                  accessLevel: input.accessLevel,
                  //defaultValue: input.defaultValue,
                }
              : font
          )
        );
        notification.success({
          message: 'Fonte atualizada',
          description: 'A fonte foi atualizada com sucesso.',
          placement: 'topRight',
          pauseOnHover: true,
          showProgress: true,
          duration: 3,
        });
      } catch (error) {
        console.error('Error editing external font:', error);
        notification.error({
          message: 'Erro ao atualizar fonte',
          description: 'Não foi possível atualizar a fonte. Tente novamente.',
          placement: 'topRight',
          pauseOnHover: true,
          showProgress: true,
          duration: 3,
        });
        throw error;
      }
    },
    []
  );
  // WebSocket for real-time test updates
  useWebSocket({
    url: `font-test/${user?.id}`,
    onMessage: (test: IExternalFontExecutionViewModel) => {
      setTests(prev => {
        const existingTestIndex = prev.findIndex(t => t.id === test.id);
        if (existingTestIndex !== -1) {
          const updatedTests = [...prev];
          updatedTests[existingTestIndex] = {
            ...updatedTests[existingTestIndex],
            ...test,
            running: false,
            executionTime: new Date(test.executionTime),
          };
          return updatedTests;
        }
        return [
          { ...test, executionTime: new Date(test.executionTime) },
          ...prev,
        ];
      });
    },
    onError: error => {
      console.error('WebSocket error:', error);
    },
  });

  // Initial fetch: basic fonts followed by details
  useEffect(() => {
    if (!logged) return;
    const fetchAll = async () => {
      await fetchExternalFonts();
      if (user.typeUser === TypeUser.ADM) await fetchExternalFontDetails();
    };
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, logged]);

  return (
    <ExternalFontContext.Provider
      value={{
        externalFonts,
        isLoadingFonts,
        isLoadingDetails,
        fetchExternalFonts,
        fetchExternalFontDetails,
        updateFontStatus,
        executeFontTest,
        tests,
        clearTestsForFont,
        clearFontMetrics,
        editExternalFont,
      }}
    >
      {children}
    </ExternalFontContext.Provider>
  );
};

export const useExternalFont: () => ContextProps = () =>
  useContext(ExternalFontContext);
export default ExternalFontProvider;
